import React, { createContext, useContext, useState, useEffect } from 'react';

export const COOKIE_CONSENT_KEY = 'cookie_consent';

export type ConsentStatus = 'accepted' | 'rejected' | null;

interface CookieConsentContextType {
    consent: ConsentStatus;
    hasConsent: boolean;
    setConsent: (status: 'accepted' | 'rejected') => void;
    resetConsent: () => void;
}

const CookieConsentContext = createContext<CookieConsentContextType | null>(null);

export function CookieConsentProvider({ children }: { children: React.ReactNode }) {
    const [consent, setConsentState] = useState<ConsentStatus>(null);

    useEffect(() => {
        const stored = localStorage.getItem(COOKIE_CONSENT_KEY) as ConsentStatus | null;
        if (stored === 'accepted' || stored === 'rejected') {
            setConsentState(stored);
        } else {
            setConsentState(null);
        }
    }, []);

    const setConsent = (status: 'accepted' | 'rejected') => {
        localStorage.setItem(COOKIE_CONSENT_KEY, status);
        setConsentState(status);
    };

    const resetConsent = () => {
        localStorage.removeItem(COOKIE_CONSENT_KEY);
        setConsentState(null);
    };

    const hasConsent = consent === 'accepted';

    return (
        <CookieConsentContext.Provider value={{ consent, hasConsent, setConsent, resetConsent }}>
            {children}
        </CookieConsentContext.Provider>
    );
}

export function useCookieConsent() {
    const context = useContext(CookieConsentContext);
    if (!context) {
        throw new Error('useCookieConsent must be used within CookieConsentProvider');
    }
    return context;
}
